import { SplitTreatmentName } from "@yardzen/splitio";
import { BRAND_LOGO_CDN_BASE } from "../imagesCdn";

/**
 * Front-end co-brand registry, keyed by `BuilderPartner.key`.
 *
 * The partner's display name is authoritative from the API (co-branded chrome
 * resolves it per project via useActiveBuilderPartner); the entry here is only
 * the fallback name shown before the API resolves. Visual theming is supplied
 * by each partner route group's own `--partner-*` CSS tokens (see
 * toll-theme.css).
 *
 * This stays a front-end registry until per-partner branding fields (logo,
 * theme tokens) land on the BuilderPartner model; add one entry per partner.
 */
export interface PartnerBranding {
  /** Fallback display name, used only until the API context resolves. */
  displayName: string;
  mark?: {
    src: string;
    width: number;
    height: number;
    /** The image is the full co-brand lockup (partner + Yardzen), so the header shows it alone. */
    lockup?: boolean;
  };
  /**
   * Route-group prefix of the partner's co-branded onboarding flow, and the
   * split that gates login/dashboard-entry routing into it. Both are required
   * to route a partner's clients into the co-branded flow; omit them until the
   * flow has shipped and clients fall back to standard onboarding. Keeping the
   * split name here (rather than in the routing logic) is what keeps that logic
   * partner-agnostic.
   */
  onboardingBasePath?: string;
  onboardingSplit?: SplitTreatmentName;
}

export const PARTNER_BRANDING: Record<string, PartnerBranding> = {
  "toll-brothers": {
    displayName: "Toll Brothers",
    mark: {
      src: `${BRAND_LOGO_CDN_BASE}/brand-toll-brothers-yardzen.png`,
      width: 5169,
      height: 683,
      lockup: true,
    },
    onboardingBasePath: "/toll",
    onboardingSplit: SplitTreatmentName.TOLL_ONBOARDING,
  },
};

export function getPartnerBranding(
  key: string | null | undefined,
): PartnerBranding | null {
  return key ? (PARTNER_BRANDING[key] ?? null) : null;
}
